import { iconAccountCircle, iconMenu } from "ui/components/icons";
import AppIcon from "ui/components/icons/AppIcon";
import { alpha, useMediaQuery } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { styled, useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import logo from "assets/images/cam-logo.png";
import { useActionItemsStore } from "data/store/ActionItems";
import { useAuthStore } from "data/store/Auth";
import { useBulkMoveStore } from "data/store/BulkMove";
import { applicationSupportEmail } from "data/store/EnvConfig";
import React, {
  lazy,
  MouseEvent,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShortcutTag } from "ui/components/data-display/ShortcutTag";
import { useKeyboardShortcut } from "ui/hooks/useKeyboardShortcut";
import SearchInput from "ui/partials/global-search/SearchInput";
import { logout } from "utils/logout";
import { GlobalSearchProvider } from "../global-search/SearchProvider";
import ProfileButton from "./ProfileButton";
import UserMenu from "./UserMenu";

const ActionItemsDrawer = lazy(
  () => import("./action-items/drawer/ActionItemsDrawer"),
);
const ActionItemsDrawerSkeleton = lazy(
  () => import("./action-items/drawer/ActionItemsDrawerSkeleton"),
);

const DenseToolbar = styled(Toolbar)(({ theme }) => ({
  position: "relative",
  "&.MuiToolbar-dense": {
    padding: theme.spacing(0, 0.5),
    minHeight: "40px",
  },
}));

const menuId = "primary-search-account-menu";

type NavBarProps = {
  progress?: React.ReactNode;
};

const useNavbarVisibility = (isMobile: boolean) => {
  const [showNavbar, setShowNavbar] = useState(true);
  const lastScrollY = useRef(window.scrollY);

  useEffect(() => {
    const showHideNavbar = () => {
      const currentScrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (currentScrollY <= 0) {
        setShowNavbar(true);
      } else if (windowHeight + currentScrollY >= documentHeight - 1) {
        setShowNavbar(false);
      } else if (currentScrollY > lastScrollY.current) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }
      lastScrollY.current = currentScrollY;
    };

    if (isMobile) {
      window.addEventListener("scroll", showHideNavbar);
      return () => window.removeEventListener("scroll", showHideNavbar);
    } else {
      setShowNavbar(true);
      window.removeEventListener("scroll", showHideNavbar);
    }
  }, [isMobile, setShowNavbar, lastScrollY]);

  return showNavbar;
};

const useProfileMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);
  const sendFeedbackEmail = () => {
    window.open(`mailto:${applicationSupportEmail()}`);
    handleMenuClose();
  };
  return {
    anchorEl,
    isMenuOpen: Boolean(anchorEl),
    handleProfileMenuOpen,
    handleMenuClose,
    sendFeedbackEmail,
  };
};

const NavBar = ({ progress }: NavBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { username, isAdmin } = useAuthStore();
  const navigate = useNavigate();
  const { actionItemsDrawerOpen, setActionItemsDrawerOpen } =
    useActionItemsStore();
  const handleActionItemsDrawerOpen = () => setActionItemsDrawerOpen(true);
  const handleActionItemsDrawerClose = () => setActionItemsDrawerOpen(false);
  const {
    anchorEl,
    isMenuOpen,
    handleProfileMenuOpen,
    handleMenuClose,
    sendFeedbackEmail,
  } = useProfileMenu();
  const showNavbar = useNavbarVisibility(isMobile);

  useKeyboardShortcut({
    ctrl: true,
    key: "q",
    action: actionItemsDrawerOpen
      ? handleActionItemsDrawerClose
      : handleActionItemsDrawerOpen,
  });

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        data-test-id={isMobile ? "navbar-mobile" : "navbar"}
        position={isMobile ? "fixed" : "static"}
        elevation={0}
        style={{
          transition: "transform 0.3s ease-in-out",
          transform: showNavbar ? "translateY(0)" : "translateY(-100%)",
        }}
      >
        <DenseToolbar variant="dense">
          {/* Menu Button Expanded */}
          <Box sx={{ pr: 2, display: { xs: "none", md: "flex" } }}>
            <nav>
              <Link to="/" tabIndex={-1}>
                <Button style={{ padding: "0 1em 0 0", color: "white" }}>
                  <img id="nav-logo" alt="CA Logo" height="32" src={logo} />

                  <Typography
                    variant="button"
                    noWrap
                    fontSize="16px"
                    fontWeight="400"
                    sx={{
                      pl: 2,
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.primary.main
                          : theme.palette.common.white,
                    }}
                  >
                    Research Central
                  </Typography>
                </Button>
              </Link>
            </nav>
          </Box>

          {/* Action Items */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              textAlign: "center",
              ml: 3,
            }}
          >
            <Button
              color="inherit"
              data-test-id="burger-icon"
              aria-label="open drawer"
              onClick={handleActionItemsDrawerOpen}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <AppIcon icon={iconMenu} />

                <Typography
                  sx={{
                    textTransform: "uppercase",
                    fontSize: "16px",
                    display: { xs: "none", md: "flex" },
                    ml: 0.7,
                  }}
                >
                  Action Items
                </Typography>

                <ShortcutTag
                  testId="action-items-shortcut-tag"
                  shortcut="q"
                  bgColor={alpha(theme.palette.primary.light, 0.7)}
                />
              </Box>
            </Button>
          </Box>

          {/* Empty Space */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Global Search */}
          <GlobalSearchProvider>
            <SearchInput />
          </GlobalSearchProvider>

          {/* Empty Space */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Profile Menu Expanded */}
          <Box sx={{ display: { xs: "none", md: "flex" }, pl: 2 }}>
            <ProfileButton
              username={username}
              menuId={menuId}
              onClick={handleProfileMenuOpen}
            />
          </Box>

          {/* Profile Menu Collapsed */}
          <Box sx={{ display: { xs: "flex", md: "none" }, pl: 2 }}>
            <IconButton
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AppIcon icon={iconAccountCircle} />
            </IconButton>
          </Box>
          {showNavbar && progress && (
            <Box
              sx={{ position: "absolute", top: "100%", left: 0, width: "100%" }}
            >
              {progress}
            </Box>
          )}
        </DenseToolbar>
      </AppBar>

      <UserMenu
        anchorEl={anchorEl}
        isMenuOpen={isMenuOpen}
        username={username}
        menuId={menuId}
        onClose={handleMenuClose}
        onBulkMove={
          isAdmin
            ? () => {
                useBulkMoveStore.getState().reset();
                navigate("/bulk-move");
                handleMenuClose();
              }
            : undefined
        }
        onSendFeedback={sendFeedbackEmail}
        onLogout={() => {
          logout();
          handleMenuClose();
          navigate("/");
        }}
      />

      <Suspense fallback={<ActionItemsDrawerSkeleton />}>
        <ActionItemsDrawer
          open={actionItemsDrawerOpen}
          onClose={handleActionItemsDrawerClose}
        />
      </Suspense>
    </Box>
  );
};

export default NavBar;
